import { useCallback, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { callPost } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import { TeaserBlock } from '@/shared/types/teaser';

/**
 * Builds a preview of the customer's page with the widget on it.
 *
 * The POST is the slow half — a headless render on the other side — so it is a
 * mutation with a real pending state, and the URL it yields is what both the
 * modal's iframe and the "open in a new tab" link point at.
 */

type PreviewResponse = {
  preview_id: string;
  /** The customer's own pathname, which the preview URL has to carry. */
  path: string;
  integration_active: boolean;
};

type StartArgs = {
  url: string;
  /** The author's unsaved teaser, so the preview shows edits before a save. */
  teaser: TeaserBlock | null;
};

/**
 * The failure codes the route answers with that are states, not errors, and so
 * need their own sentence rather than a generic one.
 */
const MESSAGE_KEYS: Record<string, string> = {
  blocked_host: 'integrations.web_page_preview.errors.invalid_url',
  invalid_url: 'integrations.web_page_preview.errors.invalid_url',
  render_busy: 'integrations.web_page_preview.errors.busy',
  no_usable_content: 'integrations.web_page_preview.errors.no_content',
  waf_blocked: 'integrations.web_page_preview.errors.waf_blocked',
  preview_expired: 'integrations.web_page_preview.errors.expired',
  render_timeout: 'integrations.web_page_preview.errors.timeout',
  invalid_teaser: 'integrations.web_page_preview.errors.teaser_too_large',
  too_large: 'integrations.web_page_preview.errors.page_too_large',
  not_found: 'integrations.web_page_preview.errors.not_found',
};

/** `fetcher` spreads the error payload onto the Error, so `code` is on it. */
const readErrorCode = (error: unknown): string | undefined => (error as { code?: string })?.code;

export const useWebchatPreview = (deskId?: string, integrationId?: string) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation<PreviewResponse, Error, StartArgs>({
    mutationFn: ({ url, teaser }) =>
      callPost(
        `/www/webchat-preview/${encodeURIComponent(deskId ?? '')}/${encodeURIComponent(integrationId ?? '')}`,
        { url, teaser }
      ),
    onSuccess: (data) => {
      setErrorMessage(null);
      // The customer's pathname is mirrored into our URL on purpose: the widget
      // matches `pages[].url` against the host document's location, so without
      // it a hint like `/shop` never matches and no page teaser appears.
      const path = data.path.replace(/^\/+/, '');
      /*
        `account_slug` has to be in the URL, not a header.

        The document is loaded by an `<iframe src>` and opened in a new tab, and
        neither can carry `X-Moveo-Account-Slug` the way `fetcher` does — so the
        server could not resolve the account and answered 403 with helmet's own
        CSP, before the route ever ran. `extractAccountSlug` reads the query
        parameter too, which is what makes this work from a plain navigation.
      */
      const query = new URLSearchParams({ account_slug: slug ?? '' });
      setPreviewUrl(
        `/www/webchat-preview/${encodeURIComponent(deskId ?? '')}/${encodeURIComponent(integrationId ?? '')}/page/${data.preview_id}/${path}?${query}`
      );
    },
    onError: (error) => {
      setPreviewUrl(null);
      const key = MESSAGE_KEYS[readErrorCode(error) ?? ''];
      setErrorMessage(
        key ? t(key) : error.message || t('integrations.web_page_preview.errors.failed')
      );
    },
  });

  const reset = useCallback(() => {
    setPreviewUrl(null);
    setErrorMessage(null);
    mutation.reset();
  }, [mutation]);

  return {
    /** Renders and, on success, yields the URL the preview tab navigates to. */
    start: mutation.mutate,
    isPending: mutation.isPending,
    previewUrl,
    errorMessage,
    reset,
  };
};
